import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberRangeDialogComponent, NumberRangeDialogData } from './number-range-dialog/number-range-dialog.component';
import { NumberRange } from './number-range.model';
import { NumberRangeService } from './number-range.service';

@Component({
  selector: 'app-number-ranges',
  templateUrl: './number-ranges.component.html',
  styleUrls: ['./number-ranges.component.scss'],
  imports: [
    MatCardModule, MatTableModule, MatSortModule, MatPaginatorModule, MatFormFieldModule, MatInputModule,
    MatButtonModule, MatIconModule, MatTooltipModule, MatProgressBarModule, MatDialogModule, MatSnackBarModule
  ]
})
export class NumberRangesComponent implements OnInit, AfterViewInit {

  displayedColumns: string[] = ['key', 'min', 'max', 'actual', 'actions'];
  dataSource = new MatTableDataSource<NumberRange>([]);
  loading = false;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private numberRangeService: NumberRangeService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.load();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  load() {
    this.loading = true;
    this.numberRangeService.getAll().subscribe({
      next: (items) => {
        this.dataSource.data = [...items].sort((a, b) => a.key.localeCompare(b.key));
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.showError(err, 'Loading number ranges failed.');
      }
    });
  }

  applyFilter(event: Event) {
    this.dataSource.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.paginator?.firstPage();
  }

  create() {
    this.openDialog({}).subscribe(result => {
      if (result) {
        this.dataSource.data = [result, ...this.dataSource.data];
        this.notify(`Number range ${result.key} created.`);
      }
    });
  }

  edit(numberRange: NumberRange) {
    this.openDialog({ numberRange }).subscribe(result => {
      if (result) {
        this.replaceRow(result);
        this.notify(`Number range ${result.key} updated.`);
      }
    });
  }

  increment(numberRange: NumberRange) {
    this.numberRangeService.increment(numberRange.key).subscribe({
      next: (result) => {
        this.replaceRow(result);
        this.notify(`Number range ${result.key} incremented to ${result.actual}.`);
      },
      error: (err) => this.showError(err, 'Increment failed.')
    });
  }

  delete(numberRange: NumberRange) {
    if (!confirm(`Delete number range "${numberRange.key}"?`)) {
      return;
    }
    this.numberRangeService.delete(numberRange.key).subscribe({
      next: () => {
        this.dataSource.data = this.dataSource.data.filter(item => item.key !== numberRange.key);
        this.notify(`Number range ${numberRange.key} deleted.`);
      },
      error: (err) => this.showError(err, 'Delete failed.')
    });
  }

  private openDialog(data: NumberRangeDialogData) {
    return this.dialog.open<NumberRangeDialogComponent, NumberRangeDialogData, NumberRange>(NumberRangeDialogComponent, {
      width: '480px',
      maxWidth: '96vw',
      disableClose: true,
      autoFocus: false,
      data
    }).afterClosed();
  }

  private replaceRow(updated: NumberRange) {
    this.dataSource.data = this.dataSource.data.map(item => item.key === updated.key ? updated : item);
  }

  private notify(message: string) {
    this.snackBar.open(message, '', { duration: 2000, panelClass: 'hintMsg' });
  }

  private showError(err: HttpErrorResponse, fallback: string) {
    const message = typeof err.error === 'string' && err.error ? err.error : fallback;
    this.snackBar.open(message, 'Close', { duration: 5000 });
  }
}
